<script setup lang="ts">
import { index } from '@/routes/ai/transactions/reports';
import { useAiReportGenerationStore } from '@/stores/aiReportGenerationStore';
import type { AiReportGeneration } from '@/types/ai-report-generation';
import { Link, router, usePage } from '@inertiajs/vue3';
import { LoaderCircle } from 'lucide-vue-next';
import { onMounted, onUnmounted, watch } from 'vue';
const page = usePage();
const tracker = useAiReportGenerationStore();
watch(
    () => [page.props.auth?.user?.id, page.props.app?.household?.id],
    ([user, household]) => tracker.setContext(user, household),
    { immediate: true, flush: 'sync' },
);
watch(
    () => page.component,
    (component) => tracker.setComponent(component),
    { immediate: true, flush: 'sync' },
);
onMounted(() => {
    const stopFlash = router.on('flash', (event) => {
        const item = (event.detail.flash as Record<string, unknown>)
            .ai_report_generation as AiReportGeneration | undefined;
        if (item) tracker.accepted(item);
    });
    const wake = () => void tracker.reconcile();
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('focus', wake);
    let channel: BroadcastChannel | undefined;
    const storageWake = (event: StorageEvent) => {
        if (event.key === 'finzzo-ai-report-generations') wake();
    };
    if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel('finzzo-ai-report-generations');
        channel.onmessage = wake;
        tracker.connectSignal(() => channel?.postMessage('changed'));
    } else {
        window.addEventListener('storage', storageWake);
        tracker.connectSignal(() => {
            try {
                localStorage.setItem(
                    'finzzo-ai-report-generations',
                    crypto.randomUUID(),
                );
            } catch {
                /* Focus remains a fallback when storage is unavailable. */
            }
        });
    }
    onUnmounted(() => {
        stopFlash();
        channel?.close();
        tracker.clear();
        document.removeEventListener('visibilitychange', wake);
        window.removeEventListener('online', wake);
        window.removeEventListener('focus', wake);
        window.removeEventListener('storage', storageWake);
        tracker.connectSignal(() => {});
    });
});
</script>
<template>
    <div
        v-if="tracker.context && (tracker.activeCount || tracker.unavailable)"
        role="status"
        aria-live="polite"
        class="fixed right-4 bottom-4 z-40 max-w-[calc(100vw-2rem)]"
    >
        <Link
            :href="index().url"
            class="border-border bg-surface text-text-soft inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm"
        >
            <LoaderCircle
                v-if="tracker.activeCount && !tracker.unavailable"
                class="h-4 w-4 animate-spin"
                aria-hidden="true"
            />
            {{
                tracker.unavailable
                    ? $t('ai.generation_tracking_unavailable')
                    : $t('ai.generation_active_count', {
                          count: String(tracker.activeCount),
                      })
            }}
        </Link>
    </div>
</template>
